import { CategoryQuestions } from '../types';

export const womensEmpowermentQuestions: CategoryQuestions = {
  title: 'Personalize Your Empowerment Session',
  questions: {
    primaryFocus: {
      label: 'What area of empowerment do you want to focus on today?',
      type: 'radio',
      options: [
        'Setting healthy boundaries without guilt',
        'Self-advocacy and speaking up for my needs',
        'Breaking people-pleasing patterns',
        'Reclaiming personal power and confidence',
        'Body autonomy and self-trust',
        'Work-life balance and career confidence',
        'Other'
      ]
    },
    primaryFocusOther: {
      label: 'Please specify your focus area',
      type: 'text',
      placeholder: 'Describe what you want to focus on...',
      conditional: {
        field: 'primaryFocus',
        value: 'Other'
      }
    },
    currentChallenges: {
      label: 'What challenges are you facing in empowerment right now? (Optional)',
      type: 'textarea',
      placeholder: 'Share what you\'re working through - this helps personalize your session...'
    },
    relationshipPatterns: {
      label: 'What relationship pattern would you like to shift?',
      type: 'radio',
      options: [
        'Over-giving and exhaustion',
        'Difficulty saying no',
        'Minimizing my own needs',
        'Perfectionism and self-criticism',
        'Fear of disappointing others',
        'Taking on others\' emotions',
        'Other'
      ]
    },
    relationshipPatternsOther: {
      label: 'Please specify the relationship pattern',
      type: 'text',
      placeholder: 'Describe the pattern you want to shift...',
      conditional: {
        field: 'relationshipPatterns',
        value: 'Other'
      }
    },
    empowermentGoal: {
      label: 'What do you want to strengthen today?',
      type: 'radio',
      options: [
        'Trust in my intuition and inner wisdom',
        'Confidence in my decisions',
        'Permission to prioritize myself',
        'Voice and assertive communication',
        'Self-compassion and kindness',
        'Releasing shame and reclaiming worth',
        'Other'
      ]
    },
    empowermentGoalOther: {
      label: 'Please specify what you want to strengthen',
      type: 'text',
      placeholder: 'Describe your empowerment goal...',
      conditional: {
        field: 'empowermentGoal',
        value: 'Other'
      }
    },
    emotionalTone: {
      label: 'What emotional tone serves you best?',
      type: 'radio',
      options: [
        'Fierce and protective - permission to put myself first',
        'Gentle and compassionate - healing inner wounds',
        'Direct and motivating - calling out my strength',
        'Validating and affirming - acknowledging the struggle',
        'Grounding and centering - building inner safety',
        'Other'
      ]
    },
    emotionalToneOther: {
      label: 'Please specify your preferred emotional tone',
      type: 'text',
      placeholder: 'Describe the emotional tone that serves you best...',
      conditional: {
        field: 'emotionalTone',
        value: 'Other'
      }
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for you?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  practitionerTitle: 'Personalize Empowerment Session for Your Client',
  practitionerQuestions: {
    primaryFocus: {
      label: 'What area of empowerment does your client want to focus on?',
      type: 'radio',
      options: [
        'Setting healthy boundaries without guilt',
        'Self-advocacy and speaking up for her needs',
        'Breaking people-pleasing patterns',
        'Reclaiming personal power and confidence',
        'Body autonomy and self-trust',
        'Work-life balance and career confidence',
        'Other'
      ]
    },
    primaryFocusOther: {
      label: 'Please specify your client\'s focus area',
      type: 'text',
      placeholder: 'Describe what your client wants to focus on...',
      conditional: {
        field: 'primaryFocus',
        value: 'Other'
      }
    },
    currentChallenges: {
      label: 'What challenges is your client facing in empowerment? (Optional)',
      type: 'textarea',
      placeholder: 'Share what your client is working through...'
    },
    relationshipPatterns: {
      label: 'What relationship pattern would your client like to shift?',
      type: 'radio',
      options: [
        'Over-giving and exhaustion',
        'Difficulty saying no',
        'Minimizing her own needs',
        'Perfectionism and self-criticism',
        'Fear of disappointing others',
        'Taking on others\' emotions',
        'Other'
      ]
    },
    relationshipPatternsOther: {
      label: 'Please specify the relationship pattern',
      type: 'text',
      placeholder: 'Describe the pattern your client wants to shift...',
      conditional: {
        field: 'relationshipPatterns',
        value: 'Other'
      }
    },
    empowermentGoal: {
      label: 'What does your client want to strengthen?',
      type: 'radio',
      options: [
        'Trust in her intuition and inner wisdom',
        'Confidence in her decisions',
        'Permission to prioritize herself',
        'Voice and assertive communication',
        'Self-compassion and kindness',
        'Releasing shame and reclaiming worth',
        'Other'
      ]
    },
    empowermentGoalOther: {
      label: 'Please specify what your client wants to strengthen',
      type: 'text',
      placeholder: 'Describe your client\'s empowerment goal...',
      conditional: {
        field: 'empowermentGoal',
        value: 'Other'
      }
    },
    emotionalTone: {
      label: 'What emotional tone would serve your client best?',
      type: 'radio',
      options: [
        'Fierce and protective - permission to put herself first',
        'Gentle and compassionate - healing inner wounds',
        'Direct and motivating - calling out her strength',
        'Validating and affirming - acknowledging the struggle',
        'Grounding and centering - building inner safety',
        'Other'
      ]
    },
    emotionalToneOther: {
      label: 'Please specify the emotional tone for your client',
      type: 'text',
      placeholder: 'Describe the emotional tone that would serve your client best...',
      conditional: {
        field: 'emotionalTone',
        value: 'Other'
      }
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your client?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    }
  },
  groupTitle: 'Personalize Your Group Empowerment Session',
  groupQuestions: {
    groupContext: {
      label: 'What type of group empowerment session are you creating?',
      type: 'radio',
      options: [
        'Women\'s support circle',
        'Professional development group',
        'Therapy or healing group',
        'Mother\'s support group',
        'Leadership development cohort',
        'Body positivity group',
        'Boundary-setting workshop',
        'Other'
      ]
    },
    groupContextOther: {
      label: 'Please specify your group type',
      type: 'text',
      placeholder: 'Describe your group context...',
      conditional: {
        field: 'groupContext',
        value: 'Other'
      }
    },
    groupFocus: {
      label: 'What is your group\'s primary empowerment goal?',
      type: 'radio',
      options: [
        'Learning to set boundaries together',
        'Building collective confidence',
        'Supporting each other\'s self-advocacy',
        'Healing people-pleasing patterns',
        'Reclaiming power after trauma',
        'Professional growth and leadership',
        'Other'
      ]
    },
    groupFocusOther: {
      label: 'Please specify your group\'s focus',
      type: 'text',
      placeholder: 'Describe your group\'s empowerment goal...',
      conditional: {
        field: 'groupFocus',
        value: 'Other'
      }
    },
    groupDynamics: {
      label: 'How does your group work best together?',
      type: 'radio',
      options: [
        'Open sharing and mutual support',
        'Individual practice with group energy',
        'Partner exercises and accountability',
        'Silent reflection with optional sharing',
        'Facilitated discussion with personal application',
        'Other'
      ]
    },
    groupDynamicsOther: {
      label: 'Please specify your group dynamics',
      type: 'text',
      placeholder: 'Describe how your group works best together...',
      conditional: {
        field: 'groupDynamics',
        value: 'Other'
      }
    },
    sharedChallenges: {
      label: 'What challenges does your group face together? (Optional)',
      type: 'textarea',
      placeholder: 'Describe shared struggles with boundaries, self-advocacy, or empowerment...'
    },
    groupStrengths: {
      label: 'What are your group\'s collective strengths?',
      type: 'radio',
      options: [
        'Mutual support and understanding',
        'Vulnerability and honesty',
        'Shared commitment to growth',
        'Diverse perspectives and wisdom',
        'Professional expertise and guidance',
        'Other'
      ]
    },
    groupStrengthsOther: {
      label: 'Please specify your group\'s strengths',
      type: 'text',
      placeholder: 'Describe your group\'s collective strengths...',
      conditional: {
        field: 'groupStrengths',
        value: 'Other'
      }
    },
    additionalNotes: {
      label: 'Is there anything else the AI should know to create the perfect session for your group?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  },
  giftTitle: 'Personalize Empowerment Session for Your Loved One',
  giftQuestions: {
    primaryFocus: {
      label: 'What area of empowerment would help them most?',
      type: 'radio',
      options: [
        'Setting healthy boundaries without guilt',
        'Self-advocacy and speaking up for their needs',
        'Breaking people-pleasing patterns',
        'Reclaiming personal power and confidence',
        'Body autonomy and self-trust',
        'Work-life balance and career confidence',
        'Other'
      ]
    },
    primaryFocusOther: {
      label: 'Please specify their focus area',
      type: 'text',
      placeholder: 'Describe what they need to focus on...',
      conditional: {
        field: 'primaryFocus',
        value: 'Other'
      }
    },
    currentChallenges: {
      label: 'What challenges are they facing? (Optional)',
      type: 'textarea',
      placeholder: 'Share what they\'re working through - this helps personalize the session...'
    },
    relationshipPatterns: {
      label: 'What relationship pattern would they benefit from shifting?',
      type: 'radio',
      options: [
        'Over-giving and exhaustion',
        'Difficulty saying no',
        'Minimizing their own needs',
        'Perfectionism and self-criticism',
        'Fear of disappointing others',
        'Taking on others\' emotions',
        'Other'
      ]
    },
    relationshipPatternsOther: {
      label: 'Please specify the relationship pattern',
      type: 'text',
      placeholder: 'Describe the pattern they want to shift...',
      conditional: {
        field: 'relationshipPatterns',
        value: 'Other'
      }
    },
    empowermentGoal: {
      label: 'What would you like this session to strengthen for them?',
      type: 'radio',
      options: [
        'Trust in their intuition and inner wisdom',
        'Confidence in their decisions',
        'Permission to prioritize themselves',
        'Voice and assertive communication',
        'Self-compassion and kindness',
        'Releasing shame and reclaiming worth',
        'Other'
      ]
    },
    empowermentGoalOther: {
      label: 'Please specify what you want to strengthen for them',
      type: 'text',
      placeholder: 'Describe your empowerment goal for them...',
      conditional: {
        field: 'empowermentGoal',
        value: 'Other'
      }
    },
    additionalNotes: {
      label: 'Anything else the AI should know to create a perfect session for them?',
      type: 'textarea',
      placeholder: 'Any additional context, special requests, or details that would make this session more meaningful...'
    },
  }
};
